import { useTranslation } from "react-i18next";

const SUPPORTED = ["en", "es", "fr", "pl"] as const;
type Lang = (typeof SUPPORTED)[number] | "pt";

/**
 * Returns the localized value of `field` from a record that may carry
 * `field_en`, `field_es`, `field_fr`, `field_pl` columns. Falls back to
 * the base (PT) field, then to empty string.
 */
export function getLocalizedField<T extends Record<string, any>>(
  record: T | null | undefined,
  field: string,
  lang: string
): string {
  if (!record) return "";
  const l = (lang || "pt").toLowerCase().split("-")[0] as Lang;
  if (l !== "pt" && SUPPORTED.includes(l as any)) {
    const v = record[`${field}_${l}`];
    if (v && String(v).trim() !== "") return String(v);
  }
  const base = record[field];
  return base ? String(base) : "";
}

/** Hook variant that reads the current i18n language. */
export function useLocalizedField() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "pt";
  return <T extends Record<string, any>>(record: T | null | undefined, field: string) =>
    getLocalizedField(record, field, lang);
}
